import { useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Campo, Estado, Painel, data, useAcao, useCarga } from '../comum';
import { Capacidades } from './Capacidades';
import { SecaoBanco } from './configuracoes/SecaoBanco';
import { SecaoDiagnostico } from './configuracoes/SecaoDiagnostico';
import { SecaoIa } from './configuracoes/SecaoIa';
import { SecaoInstalacao } from './configuracoes/SecaoInstalacao';
import { SecaoKace } from './configuracoes/SecaoKace';
import { SecaoRede } from './configuracoes/SecaoRede';
import { SecaoSeguranca } from './configuracoes/SecaoSeguranca';
import { SecaoSentinela } from './configuracoes/SecaoSentinela';
import { SecaoSincronizacao } from './configuracoes/SecaoSincronizacao';
import { SecaoVigia } from './configuracoes/SecaoVigia';
import { ScriptsHomologados } from './ScriptsHomologados';
import type { EtapaTeste, RespostaConfig } from '../tipos';

/**
 * Módulo de Configurações.
 *
 * Tudo que antes vivia no `.env.local`. O técnico que instala não é
 * necessariamente desenvolvedor e pode não ter shell na máquina do cliente.
 *
 * Segredos nunca voltam do servidor: os campos de senha e chave aparecem vazios
 * com a marca "definida", e deixá-los em branco mantém o que está gravado.
 */
export function Configuracoes({
  aoPerderSessao,
  podeEditar,
  podeHomologarScript,
}: {
  aoPerderSessao: () => void;
  podeEditar: boolean;
  podeHomologarScript: boolean;
}): ReactNode {
  const carga = useCarga<RespostaConfig>(
    () => api.get<RespostaConfig>('/api/config'),
    [],
    aoPerderSessao,
  );

  return (
    <Estado carga={carga}>
      {carga.dados !== null && (
        <Conteudo
          dados={carga.dados}
          recarregar={carga.recarregar}
          aoPerderSessao={aoPerderSessao}
          podeEditar={podeEditar}
          podeHomologarScript={podeHomologarScript}
        />
      )}
    </Estado>
  );
}

type IdDoGrupo = 'conexao' | 'inteligencia' | 'operacao' | 'seguranca' | 'manutencao';

const GRUPOS: readonly { id: IdDoGrupo; rotulo: string; ajuda: string }[] = [
  { id: 'conexao', rotulo: 'Conexão', ajuda: 'Appliance KACE e leitura direta do banco' },
  { id: 'inteligencia', rotulo: 'Inteligência', ajuda: 'Modelo de IA e o que o produto sabe fazer' },
  { id: 'operacao', rotulo: 'Operação', ajuda: 'Sincronização, vigia da fila, Sentinela e scripts' },
  { id: 'seguranca', rotulo: 'Segurança', ajuda: 'Travas de escrita e saída de rede' },
  { id: 'manutencao', rotulo: 'Manutenção', ajuda: 'Instalação, diagnóstico, backup e histórico' },
];

function Conteudo({
  dados,
  recarregar,
  aoPerderSessao,
  podeEditar,
  podeHomologarScript,
}: {
  dados: RespostaConfig;
  recarregar: () => void;
  aoPerderSessao: () => void;
  podeEditar: boolean;
  podeHomologarScript: boolean;
}): ReactNode {
  const c = dados.configuracao;
  const acao = useAcao(aoPerderSessao);

  /**
   * 🔴 Treze painéis numa rolagem só.
   *
   * O técnico que instala procura *uma* coisa — a conexão, a chave da IA, o
   * backup — e precisava percorrer a tela inteira para achar. Pior: os avisos
   * de erro e de somente-leitura ficam no topo, longe do botão que a pessoa
   * acabou de clicar (por isso são flutuantes).
   *
   * Os grupos seguem a ordem de quem instala pela primeira vez: ligar no KACE,
   * escolher o modelo, definir o que observar, apertar as travas, e só então
   * manutenção.
   */
  const [grupo, setGrupo] = useState<IdDoGrupo>('conexao');

  const salvar = (secao: string, valor: unknown, oQue: string): void => {
    void acao.rodar(async () => {
      await api.put(`/api/config/${secao}`, valor);
      recarregar();
      return `${oQue} salvo.`;
    });
  };

  return (
    <>
      {!podeEditar && (
        <Aviso tipo="alerta">
          Você pode ver a configuração, mas não alterá-la. Alterar exige perfil administrativo no
          KACE.
        </Aviso>
      )}
      {/*
        🔴 Flutuantes de propósito.
        Os botões de Salvar desta tela ficam a centenas de pixels do topo; o
        aviso renderizado aqui em cima nunca entrava no campo de visão de quem
        acabara de clicar. O erro continua sem sumir sozinho — o `useAcao` só
        expira a confirmação.
      */}
      {acao.erro !== null && (
        <Aviso tipo="erro" flutuante>
          {acao.erro}
        </Aviso>
      )}
      {acao.ok !== null && (
        <Aviso tipo="ok" flutuante>
          {acao.ok}
        </Aviso>
      )}

      {!c.seguranca.escritaLiberada && (
        <Aviso>
          <strong>Modo somente-leitura.</strong> O produto observa o KACE e propõe ações, mas não
          escreve nada nele. Nenhum script é executado e nenhum chamado é aberto.
        </Aviso>
      )}

      <nav className="abas-modo abas-config" aria-label="Seções da configuração">
        {GRUPOS.map((g) => (
          <button
            key={g.id}
            className={g.id === grupo ? 'ativo' : ''}
            aria-current={g.id === grupo ? 'page' : undefined}
            onClick={() => setGrupo(g.id)}
            title={g.ajuda}
          >
            {g.rotulo}
          </button>
        ))}
      </nav>

      {grupo === 'conexao' && (
        <>
          {/* ── KACE ────────────────────────────────────────────────── */}
          <SecaoKace c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          {/* ── Banco do KACE ───────────────────────────────────────── */}
          <SecaoBanco c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
        </>
      )}

      {grupo === 'inteligencia' && (
        <>
          {/* ── IA ──────────────────────────────────────────────────── */}
          <SecaoIa c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          {/*
            As capacidades vêm antes dos scripts homologados: homologar um
            script para uma operação que o produto não sabe fazer é trabalho
            jogado fora, e esta lista é onde isso fica visível antes de alguém
            tentar.
          */}
          <Capacidades aoPerderSessao={aoPerderSessao} />
        </>
      )}

      {grupo === 'operacao' && (
        <>
          {/* ── Sincronização ───────────────────────────────────────── */}
          <SecaoSincronizacao c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          {/* ── Vigia de operação ───────────────────────────────────── */}
          <SecaoVigia c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          {/* ── Sentinela ───────────────────────────────────────────── */}
          <SecaoSentinela c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          <ScriptsHomologados aoPerderSessao={aoPerderSessao} podeHomologar={podeHomologarScript} />
        </>
      )}

      {grupo === 'seguranca' && (
        <>
          {/* ── Segurança ───────────────────────────────────────────── */}
          <SecaoSeguranca c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          {/* ── Rede ────────────────────────────────────────────────── */}
          <SecaoRede c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
        </>
      )}

      {grupo === 'manutencao' && (
        <>
          {/* ── Instalação ──────────────────────────────────────────── */}
          <SecaoInstalacao c={c} podeEditar={podeEditar} acao={acao} salvar={salvar} />
          {/* ── Diagnóstico ─────────────────────────────────────────── */}
          <SecaoDiagnostico
            c={c}
            acao={acao}
            boot={dados.boot}
            podeEditar={podeEditar}
            salvar={salvar}
          />
          <BackupERestauracao aoPerderSessao={aoPerderSessao} />
          {/* ── Histórico ───────────────────────────────────────────── */}
          <HistoricoConfig aoPerderSessao={aoPerderSessao} />
        </>
      )}
    </>
  );
}

/**
 * Cópia de segurança.
 *
 * O inventário volta com uma sincronização. O que só existe aqui não volta:
 * regras escritas à mão, decisões de aprovação com autor e data, histórico de
 * medições que alimenta as projeções, configuração de integrações.
 *
 * A restauração fica atrás de três travas e de uma conferência que roda ANTES
 * de o botão habilitar — é o único momento em que ainda dá para desistir com
 * informação na mão.
 */
function BackupERestauracao({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [arquivo, setArquivo] = useState<unknown>(null);
  const [conferencia, setConferencia] = useState<{
    pode: boolean;
    problemas: readonly string[];
    geradoEm: string | null;
    aviso: string | null;
    total: number;
    linhas: readonly { tabela: string; linhas: number }[];
  } | null>(null);

  const baixar = (): void => {
    void acao.rodar(async () => {
      const b = await api.get<Record<string, unknown>>('/api/backup');
      const carimbo = new Date().toISOString().slice(0, 10);
      baixarJson(b, `backup-${carimbo}.json`);
      return 'Backup baixado. Guarde-o separado da chave mestra.';
    });
  };

  const escolher = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0];
    if (f === undefined) return;
    setConferencia(null);
    setArquivo(null);

    void acao.rodar(async () => {
      const texto = await f.text();
      let lido: unknown;
      try {
        lido = JSON.parse(texto);
      } catch {
        // Arquivo trocado é o engano mais provável aqui.
        throw new Error('Este arquivo não é um JSON válido.');
      }
      const c = await api.post<typeof conferencia>('/api/backup/conferir', {
        backup: lido,
      });
      setArquivo(lido);
      setConferencia(c);
      return null;
    });
  };

  const restaurar = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ linhas: number; aviso: string }>('/api/backup/restaurar', {
        backup: arquivo,
        confirmo: true,
      });
      setArquivo(null);
      setConferencia(null);
      return `${r.linhas} linha(s) restauradas. ${r.aviso}`;
    });
  };

  return (
    <Painel titulo="Backup">
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      <p className="suave" style={{ marginTop: 0 }}>
        O inventário volta com uma sincronização. Regras, decisões de aprovação, histórico de
        medições e integrações só existem aqui.
      </p>

      <Aviso tipo="alerta">
        <strong>A chave mestra não vai no arquivo.</strong> As senhas saem cifradas e ficam
        ilegíveis sem ela — restaurar em outra instalação devolve o banco inteiro, mas as
        credenciais precisarão ser redigitadas. Guarde a chave separada do backup: juntos, os dois
        entregam o acesso ao KACE de quem tiver a cópia.
      </Aviso>

      <p className="suave">
        O detalhe de inventário por máquina (software instalado, discos, campos customizados){' '}
        <strong>não entra</strong> no arquivo — ele é reconstruído na primeira sincronização. Depois
        de restaurar, a ficha das máquinas fica sem essas seções até o próximo sync.
      </p>

      <button className="primario encolhe" disabled={acao.executando} onClick={baixar}>
        Baixar backup agora
      </button>

      <h4 style={{ marginTop: 24 }}>Backup automático</h4>
      <BackupAutomatico aoPerderSessao={aoPerderSessao} />

      <h4 style={{ marginTop: 24 }}>Restaurar</h4>
      <p className="suave">
        Apaga <strong>todos</strong> os dados atuais e substitui pelos do arquivo. Não tem volta.
      </p>

      <input type="file" accept="application/json,.json" onChange={escolher} />

      {conferencia !== null && (
        <div style={{ marginTop: 14 }}>
          {conferencia.pode ? (
            <>
              <Aviso tipo="info">
                Arquivo válido, gerado em {data(conferencia.geradoEm ?? '')}.{' '}
                <strong>{conferencia.total}</strong> linha(s) serão restauradas.
              </Aviso>
              <details>
                <summary className="suave">O que vem no arquivo</summary>
                <table>
                  <tbody>
                    {conferencia.linhas.map((l) => (
                      <tr key={l.tabela}>
                        <td>{l.tabela}</td>
                        <td>{l.linhas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
              <button
                className="perigo encolhe"
                disabled={acao.executando}
                style={{ marginTop: 12 }}
                onClick={restaurar}
              >
                Apagar tudo e restaurar este backup
              </button>
            </>
          ) : (
            <Aviso tipo="erro">
              <strong>Este backup não pode ser restaurado aqui.</strong>
              <ul>
                {conferencia.problemas.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </Aviso>
          )}
        </div>
      )}
    </Painel>
  );
}

/**
 * Backup automático diário.
 *
 * ⚠️ **Grava no mesmo disco do banco.** A tela diz isso com todas as letras:
 * um cliente que acredita estar coberto e não está fica pior que um que sabe
 * não ter backup — o primeiro não procura solução.
 */
function BackupAutomatico({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const carga = useCarga<{
    readonly arquivos: readonly { nome: string; bytes: number; em: string }[];
    readonly automatico: boolean;
    readonly horaDoDia: number;
    readonly manterUltimos: number;
    readonly aviso: string;
  }>(() => api.get('/api/backup/arquivos'), [], aoPerderSessao);

  const salvar = (campos: Record<string, unknown>): void => {
    void acao.rodar(async () => {
      await api.put('/api/config/backup', {
        automatico: carga.dados?.automatico ?? false,
        horaDoDia: carga.dados?.horaDoDia ?? 3,
        manterUltimos: carga.dados?.manterUltimos ?? 7,
        ...campos,
      });
      carga.recarregar();
      return 'Agendamento salvo.';
    });
  };

  const gerarAgora = (): void => {
    void acao.rodar(async () => {
      const a = await api.post<{ nome: string; bytes: number }>('/api/backup/arquivos');
      carga.recarregar();
      return `${a.nome} gravado (${Math.round(a.bytes / 1024)} KB).`;
    });
  };

  return (
    <Estado carga={carga}>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {carga.dados !== null && (
        <>
          <Aviso tipo="alerta">{carga.dados.aviso}</Aviso>

          <div className="linha">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={carga.dados.automatico}
                onChange={(e) => salvar({ automatico: e.target.checked })}
              />
              Gerar um backup por dia
            </label>

            <Campo rotulo="Hora">
              <input
                type="number"
                min={0}
                max={23}
                value={carga.dados.horaDoDia}
                onChange={(e) => salvar({ horaDoDia: Number(e.target.value) })}
              />
            </Campo>

            <Campo rotulo="Manter últimos">
              <input
                type="number"
                min={1}
                max={365}
                value={carga.dados.manterUltimos}
                onChange={(e) => salvar({ manterUltimos: Number(e.target.value) })}
              />
            </Campo>

            <button className="encolhe" disabled={acao.executando} onClick={gerarAgora}>
              Gerar agora
            </button>
          </div>

          {carga.dados.arquivos.length === 0 ? (
            <p className="suave">Nenhum backup em disco ainda.</p>
          ) : (
            <table>
              <tbody>
                {carga.dados.arquivos.map((a) => (
                  <tr key={a.nome}>
                    <td>{data(a.em)}</td>
                    <td className="suave">{Math.round(a.bytes / 1024)} KB</td>
                    <td>
                      <a href={`/api/backup/arquivos/${encodeURIComponent(a.nome)}`}>baixar</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Estado>
  );
}

/**
 * Histórico de alterações.
 *
 * Só os NOMES dos campos que mudaram, nunca os valores — a seção do KACE
 * carrega a senha cifrada, e devolvê-la entregaria o texto cifrado a quem só
 * pode ver configuração.
 */
function HistoricoConfig({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const carga = useCarga<{
    itens: readonly {
      id: number;
      secao: string;
      alteradoEm: string;
      alteradoPor: string;
      camposAlterados: readonly string[];
    }[];
  }>(() => api.get('/api/config/historico?limite=30'), [], aoPerderSessao);

  return (
    <Painel
      titulo="Histórico de alterações"
      sub="Quem mudou o quê, e quando. Em ambiente auditado isso é pergunta de auditoria, não de memória."
    >
      <Estado carga={carga}>
        {carga.dados !== null &&
          (carga.dados.itens.length === 0 ? (
            <p className="suave">Nenhuma alteração registrada.</p>
          ) : (
            <div className="rolagem">
              <table className="densa">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Seção</th>
                    <th>Por</th>
                    <th>Campos alterados</th>
                  </tr>
                </thead>
                <tbody>
                  {carga.dados.itens.map((h) => (
                    <tr key={h.id}>
                      <td className="suave">{data(h.alteradoEm)}</td>
                      <td>{h.secao}</td>
                      <td className="suave">{h.alteradoPor}</td>
                      <td className="suave">
                        {h.camposAlterados.length === 0 ? '—' : h.camposAlterados.join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Estado>
    </Painel>
  );
}

/** Baixa um JSON sem passar por servidor — o arquivo nasce e morre no navegador. */
export function baixarJson(dados: unknown, nome: string): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}
